import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { User, Globe, Building, MapPin, Mail, Phone, Save } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const SettingsPage = () => {
  const { user, locale, setLocale } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [userData, setUserData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    street: '',
    city: '',
    postal_code: '',
    country: 'Deutschland'
  });

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      const response = await axios.get(`${API}/users/me`);
      if (response.data) {
        setUserData({
          name: response.data.name || '',
          company: response.data.company || '',
          email: response.data.email || '',
          phone: response.data.phone || '',
          street: response.data.street || '',
          city: response.data.city || '',
          postal_code: response.data.postal_code || '',
          country: response.data.country || 'Deutschland'
        });
      }
    } catch (error) {
      console.error('Failed to fetch user data:', error);
      toast.error('Fehler beim Laden der Benutzerdaten');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/users/me`, userData);
      toast.success('Einstellungen erfolgreich gespeichert');
    } catch (error) {
      toast.error('Fehler beim Speichern der Einstellungen');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleLanguageChange = async (newLocale) => {
    try {
      await axios.put(`${API}/users/me`, { locale: newLocale });
      setLocale(newLocale);
      toast.success('Sprache geändert');
    } catch (error) {
      toast.error('Fehler beim Ändern der Sprache');
      console.error(error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen p-8" style={{ backgroundColor: '#0B0F14' }}>
        <div className="flex items-center justify-center h-64">
          <p style={{ color: '#94A3B8' }}>Laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-8" style={{ backgroundColor: '#0B0F14' }}>
      <div className="max-w-4xl mx-auto">
        <h2 className="text-3xl font-bold mb-8" style={{ color: '#E6EDF3', fontFamily: 'Manrope' }}>
          Einstellungen
        </h2>

        {/* Language Settings */}
        <Card className="mb-6" style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ color: '#E6EDF3' }}>
              <Globe className="w-5 h-5" style={{ color: '#7CD4FD' }} />
              Sprache
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              <Button
                onClick={() => handleLanguageChange('de')}
                variant={locale === 'de' ? 'default' : 'outline'}
                style={{
                  backgroundColor: locale === 'de' ? '#1EA0E6' : 'transparent',
                  borderColor: '#1F2937',
                  color: locale === 'de' ? '#ffffff' : '#E6EDF3'
                }}
              >
                🇩🇪 Deutsch
              </Button>
              <Button
                onClick={() => handleLanguageChange('en')}
                variant={locale === 'en' ? 'default' : 'outline'}
                style={{
                  backgroundColor: locale === 'en' ? '#1EA0E6' : 'transparent',
                  borderColor: '#1F2937',
                  color: locale === 'en' ? '#ffffff' : '#E6EDF3'
                }}
              >
                🇬🇧 English
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Personal Information */}
        <Card className="mb-6" style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ color: '#E6EDF3' }}>
              <User className="w-5 h-5" style={{ color: '#7CD4FD' }} />
              Persönliche Daten
            </CardTitle>
            <p className="text-sm mt-2" style={{ color: '#94A3B8' }}>
              Diese Daten werden im PDF-Report als Briefkopf verwendet
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block mb-2 text-sm" style={{ color: '#94A3B8' }}>
                  Name / Ansprechpartner
                </label>
                <Input
                  value={userData.name}
                  onChange={(e) => setUserData({ ...userData, name: e.target.value })}
                  placeholder="Max Mustermann"
                  style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                />
              </div>

              <div>
                <label className="block mb-2 text-sm" style={{ color: '#94A3B8' }}>
                  <Building className="w-4 h-4 inline mr-1" />
                  Firma / Werkstatt
                </label>
                <Input
                  value={userData.company}
                  onChange={(e) => setUserData({ ...userData, company: e.target.value })}
                  placeholder="Autowerkstatt GmbH"
                  style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                />
              </div>

              <div>
                <label className="block mb-2 text-sm" style={{ color: '#94A3B8' }}>
                  <Mail className="w-4 h-4 inline mr-1" />
                  E-Mail
                </label>
                <Input
                  type="email"
                  value={userData.email}
                  onChange={(e) => setUserData({ ...userData, email: e.target.value })}
                  placeholder="info@werkstatt.de"
                  style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                />
              </div>

              <div>
                <label className="block mb-2 text-sm" style={{ color: '#94A3B8' }}>
                  <Phone className="w-4 h-4 inline mr-1" />
                  Telefon
                </label>
                <Input
                  type="tel"
                  value={userData.phone}
                  onChange={(e) => setUserData({ ...userData, phone: e.target.value })}
                  placeholder="+49 123 456789"
                  style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                />
              </div>

              <div className="md:col-span-2">
                <label className="block mb-2 text-sm" style={{ color: '#94A3B8' }}>
                  <MapPin className="w-4 h-4 inline mr-1" />
                  Straße & Hausnummer
                </label>
                <Input
                  value={userData.street}
                  onChange={(e) => setUserData({ ...userData, street: e.target.value })}
                  placeholder="Musterstraße 123"
                  style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                />
              </div>

              <div>
                <label className="block mb-2 text-sm" style={{ color: '#94A3B8' }}>
                  PLZ
                </label>
                <Input
                  value={userData.postal_code}
                  onChange={(e) => setUserData({ ...userData, postal_code: e.target.value })}
                  placeholder="12345"
                  style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                />
              </div>

              <div>
                <label className="block mb-2 text-sm" style={{ color: '#94A3B8' }}>
                  Ort
                </label>
                <Input
                  value={userData.city}
                  onChange={(e) => setUserData({ ...userData, city: e.target.value })}
                  placeholder="Musterstadt"
                  style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                />
              </div>

              <div className="md:col-span-2">
                <label className="block mb-2 text-sm" style={{ color: '#94A3B8' }}>
                  Land
                </label>
                <Input
                  value={userData.country}
                  onChange={(e) => setUserData({ ...userData, country: e.target.value })}
                  placeholder="Deutschland"
                  style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <Button
                onClick={handleSave}
                disabled={saving}
                style={{ backgroundColor: '#1EA0E6', color: '#ffffff' }}
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Speichern...' : 'Speichern'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Newsletter Settings */}
        <Card className="mb-6" style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ color: '#E6EDF3' }}>
              <Mail className="w-5 h-5" />
              Newsletter-Einstellungen
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <p className="text-sm" style={{ color: '#94A3B8' }}>
                Verwalten Sie Ihre Newsletter-Abonnements
              </p>
              <Button
                onClick={async () => {
                  if (confirm('Möchten Sie sich wirklich vom Newsletter abmelden?')) {
                    try {
                      await axios.post(`${API}/newsletter/unsubscribe`, { email: user?.email });
                      toast.success('Sie wurden vom Newsletter abgemeldet');
                    } catch (error) {
                      toast.error('Fehler beim Abmelden vom Newsletter');
                    }
                  }
                }}
                variant="outline"
                style={{ borderColor: '#EF4444', color: '#EF4444' }}
                className="hover:bg-red-500/10"
              >
                Newsletter abmelden
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Account Info */}
        <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
          <CardHeader>
            <CardTitle style={{ color: '#E6EDF3' }}>Account-Informationen</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span style={{ color: '#94A3B8' }}>Benutzer-ID:</span>
                <span style={{ color: '#E6EDF3' }}>{user?.id}</span>
              </div>
              <div className="flex justify-between">
                <span style={{ color: '#94A3B8' }}>E-Mail:</span>
                <span style={{ color: '#E6EDF3' }}>{user?.email}</span>
              </div>
              <div className="flex justify-between">
                <span style={{ color: '#94A3B8' }}>Registriert seit:</span>
                <span style={{ color: '#E6EDF3' }}>
                  {new Date(user?.created_at).toLocaleDateString('de-DE')}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
